/* eslint-disable react-refresh/only-export-components */
/**
 * @fileoverview Protected route component for authentication-required pages
 * Handles route protection, loading states, and redirects
 * @author SKEMA CANADA
 */

import { Navigate, useLocation } from "react-router-dom";
import { useRouteAccess } from "@hooks/useAuth";
import { ROUTES } from "@router";
import "./ProtectedRoute.css";

/**
 * Loading spinner displayed during authentication verification
 * @returns {JSX.Element} Loading spinner with status message
 */
const AuthLoadingSpinner = () => (
  <div className="auth-loading-container">
    <div className="auth-loading-spinner">
      <div className="spinner"></div>
      <p className="auth-loading-text">Verifying authentication...</p>
    </div>
  </div>
);

/**
 * Access denied component for insufficient permissions
 * @param {Object} props - Component props
 * @param {string} props.reason - Denial reason ('AUTHENTICATION_REQUIRED' | 'INSUFFICIENT_PERMISSIONS')
 * @returns {JSX.Element} Access denied page with appropriate message
 */
const AccessDenied = ({ reason }) => (
  <div className="access-denied-container">
    <div className="access-denied-content">
      <div className="access-denied-icon">🔒</div>
      <h2 className="access-denied-title">Access Denied</h2>
      <p className="access-denied-message">
        {reason === "AUTHENTICATION_REQUIRED"
          ? "You need to be logged in to access this page."
          : "You do not have sufficient permissions to access this page."}
      </p>
    </div>
  </div>
);

/**
 * Protected route component
 * Wraps components that require authentication or specific permissions
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to protect
 * @param {Array<string>} props.requiredPermissions - Required permissions for access
 * @param {boolean} props.requireAuth - Whether authentication is required
 * @param {string} props.redirectTo - Custom redirect path for unauthenticated users
 * @param {boolean} props.showAccessDenied - Show access denied page instead of redirect
 * @returns {JSX.Element} Protected content or redirect/denial page
 */
const ProtectedRoute = ({
  children,
  requiredPermissions = [],
  requireAuth = true,
  redirectTo = ROUTES.LOGIN,
  showAccessDenied = false,
}) => {
  /**
   * Current route location for redirect state
   */
  const location = useLocation();

  /**
   * Route access verification hook
   */
  const { hasAccess, loading, reason } = useRouteAccess({
    requiredPermissions,
    requireAuth,
  });

  if (loading) {
    return <AuthLoadingSpinner />;
  }

  if (hasAccess) {
    return children;
  }

  if (showAccessDenied) {
    return <AccessDenied reason={reason} />;
  }

  // Redirect to login page with return URL
  return (
    <Navigate to={redirectTo} state={{ from: location.pathname }} replace />
  );
};

/**
 * Higher-order component for creating protected routes
 * @param {React.ComponentType} Component - Component to protect
 * @param {Object} protectionOptions - Protection configuration (requireAuth, requiredPermissions, etc.)
 * @returns {React.ComponentType} Component wrapped with protection logic
 */
export const withProtection = (Component, protectionOptions = {}) => {
  return function ProtectedComponent(props) {
    return (
      <ProtectedRoute {...protectionOptions}>
        <Component {...props} />
      </ProtectedRoute>
    );
  };
};

/**
 * Get protection requirements for a specific route
 * @param {string} pathname - Route pathname
 * @returns {Object} Protection requirements
 */
export const isProtectedRoute = (pathname) => {
  const protectedRoutes = [ROUTES.ADVANCED, ROUTES.DOWNLOADS];

  return protectedRoutes.includes(pathname);
};

/**
 * Get protection requirements for a specific route
 * @param {string} pathname - Route pathname
 * @returns {Object} Protection config with requireAuth and requiredPermissions
 * @returns {boolean} returns.requireAuth - Whether authentication is required
 * @returns {Array<string>} returns.requiredPermissions - Required permission list
 */
export const getRouteProtection = (pathname) => {
  const routeProtections = {
    [ROUTES.ADVANCED]: {
      requireAuth: true,
      requiredPermissions: [],
    },
    [ROUTES.DOWNLOADS]: {
      requireAuth: true,
      requiredPermissions: [],
    },
  };

  return (
    routeProtections[pathname] || {
      requireAuth: false,
      requiredPermissions: [],
    }
  );
};

export default ProtectedRoute;
